export default function estimateAeroCoefficients (bullet, M = [ 2.5 ], Cd = []) {
  let Cla = []
  let Clp = []
  let Cma = []
  let Cda2 = []
  let Cnpa = []
  let Cna = []
  let Cmq = []
  let z = []
  let diameter = bullet.diameter * .0254
  let l = (bullet.boattailLength + bullet.bearingSurfaceLength + bullet.noseLength) / bullet.diameter
  let l3 = (bullet.boattailLength + bullet.bearingSurfaceLength) / bullet.diameter
  let l2 = bullet.boattailLength / bullet.diameter
  let l1 = bullet.noseLength / bullet.diameter
  let f1 = (bullet.baseDiameter / bullet.diameter) * diameter
  let f2 = (bullet.meplat / bullet.diameter) * diameter
  let g1 = (bullet.boattailLength + bullet.bearingSurfaceLength + bullet.noseLength - bullet.cg) / bullet.diameter

  // Set up nose shape parameters
  let n;
  if (bullet.rtr < .1) n = 3;
  else if (bullet.rtr > .8) n = 2;
  else n = 1;

  let l10 = l1;
  let t1 = (l10 * l10 + ((1 - f2 / diameter) / 2) * ((1 - f2 / diameter) / 2)) / (1 - f2 / diameter);
  if (n === 3) {
    l1 = (diameter / (diameter - f2)) * l10;
  } else {
    l1 = Math.sqrt(t1 - .25);
    if (n === 1) l1 = (l1 + ((diameter / (diameter - f2)) * l10)) / 2;
  }
  let f3 = l1 - l10;
  g1 = g1 + f3;
  let d1 = f1 / diameter;

  // Calculation loop for each Mach number
  for (let i = 0; i < M.length; i++) {
    let mi = M[i];
    let b = Math.sqrt(Math.abs(mi * mi - 1));
    let b1 = Math.sqrt(Math.abs(mi * mi - 0.9025));
    let a = mi * mi / l1;
    let c1;
    let c2;
    let c4;
    let c5;
    let c6;
    let x1;
    let x2;
    let x3;
    let a1;
    let u7;
    let u8;
    let u1;
    let u2;
    let u3;

    // Body lift with no boattail
    if (mi > 1.19) {
      c1 = 1.974 + 0.921 * b / l1;
    } else {
      if (a <= 0.25) {
        c1 = 0.856 * a - 0.044 * l3 + 1.963;
      } else if (a <= 0.35) {
        c1 = (0.43 * a - 0.1515) * l3 + 2.202 - 0.1 * a;
      } else if (a <= 0.4) {
        c1 = (0.104 - 0.3 * a) * l3 + 0.573 * a + 1.9665;
      } else {
        c1 = (0.12 * a - 0.064) * l3 - 0.227 * a + 2.2865;
      }
    }

    // Subsonic lift loss due to the presence of a boattail
    if (mi < 0.951) {
      if (l2 < 0.48) {
        c2 = b1 * (3.115 + 15.083 * l2 * l2 - 21.106 * l2);
        c2 += 71.14601 * l2 - 47.3 * l2 * l2 - 18.303;
        c2 *= Math.pow(d1, 0.75) * (1 - Math.pow(d1, 0.75));
      } else {
        if (b1 === 0) c2 = 0;
        else c2 = (1 - d1 * d1) * 2;
      }
    } else {
      if (mi >= 0.951 && mi <= 1) c2 = (1 - d1 * d1) * 2;
      else if (l2 / b1 > 3) c2 = (1 - d1 * d1) * 2;
      else c2 = (1 - d1 * d1) * (2 - Math.pow((3 - l2 / b1), 3.2439) / 17.649);
    }

    // Supersonic boattail lift loss
    if (mi > 1.19 && l2 / b1 <= 3) c2 = (1 - d1 * d1) * (2 - Math.pow((3 - l2 / b1), 3.2439) / 17.649);

    // Supersonic lift loss corrections
    if (mi > 0.951 && mi < 2) {
      if (mi < 1.4) c2 *= 0.5 * mi + 0.41;
      else c2 *= 1.3662 - 0.1833 * mi;
    }

    // Total lift
    Cla[i] = c1 - c2;

    // Pitching moment about nose due to body and nose
    let l4;
    if (mi > 1.19) {
      l4 = 0.34 + 0.25 / b;
      if (l2 < l4 && d1 !== 1) c2 *= (1 - Math.pow(1 - (1 - d1) * l4 / l2, 2)) / (1 - Math.pow(d1, 2));
    }
    if (mi > 1.19) {
      if (b < 0.6) c4 = (0.166 * b + 1.12) * l1 + 0.05 * b + 0.14;
      else if (b < 1) c4 = (2.045 - 1.375 * b) * l1 + 4.575 * b - 2.575;
      else c4 = (0.82 - 0.15 * b) * l1 + 1.7 * b + 0.3;
    } else {
      if (b > 0.65) c4 = (1.304 - 0.846 * b) * l1 + 0.5 * b + 0.945;
      else if (b > 0.3) c4 = (1.304 - 0.846 * b) * l1 + 2.286 * b - 0.216;
      else c4 = (1.12 - 0.233 * b) * l1 + 1.1 * b + 0.14;
    }

    // Correction to pitching moment for different nose shapes
    if (n === 2) c4 = 0.456 * c4 / 0.557;
    else if (n === 3) c4 = 0.667 * c4 / 0.557;

    // Boattail moment about nose
    if (mi !== 1) x1 = (0.66 - 0.041 * l2 / b) * l2;
    else x1 = l2 / 2;
    x1 += l1 + l3 - l2;
    c5 = x1 * c2;

    // Total moment about nose
    c6 = c4 - c5;

    // Overall lift center aft of nose
    x2 = c6 / Cla[i];
    x3 = g1 - x2;
    z[i] = x2;

    // Moment about center of gravity
    Cma[i] = Cla[i] * x3;

    // Corrections to CMA
    if (mi < 0.951) u7 = 0.73 + 0.163 * mi;
    else if (mi === 1) u7 = 0.84;
    else u7 = 0.82;
    Cma[i] = u7 * Cma[i];

    // Body aspect ratio
    a1 = 1 / (l1 / 2 + l3 - l2 + l2 * (1 + d1) / 2);

    // Calculation of yaw drag
    if (mi < 0.95) {
      Cda2[i] = 1.476 + 0.467 * mi + 0.08649999 * Cla[i] * Cla[i] / a1;
    } else if (mi < 1) {
      Cda2[i] = 1.85 + (0.3825 * mi - 0.2195) * Cla[i] * Cla[i] / a1;
    } else if (mi < 1.3) {
      Cda2[i] = 9.467 * mi - 7.617 + (0.606 - 0.443 * mi) * Cla[i] * Cla[i] / a1;
    } else {
      Cda2[i] = 9.825 - 3.95 * mi + (0.1458 * mi - 0.1594) * Cla[i] * Cla[i] / a1;
    }

    // Corrections to CDA2 and CLA
    u8 = 1.33;
    Cda2[i] = u8 * Cda2[i];
    if (mi <= 1) u1 = 0.96 + 0.038 * mi;
    else u1 = 1.09 - 0.057 * mi;
    if (l3 < 1.5) u1 = u1 - 0.12 * Math.pow(1.5 - l3, 2);
    if (mi <= 0.95) u2 = (0.22 * Math.pow(mi, 2)) / Math.sqrt(1 - Math.pow(mi, 2));
    else u2 = 0.431 / mi - 0.1;
    u3 = u1 + u2 * Math.pow(l2 / d1, 2);
    Cla[i] = u3 * Cla[i];

    // from https://doi.org/10.1016/j.dt.2018.10.003
    // it appears to get in the ballpark, but is not right
    // leaving here, but we need to replace it with something better
    //Cnpa[i] = ((mi / 2) - 1) * ((l * diameter) / (4 * f1)) * (((l * diameter) - (g1 * diameter)) / diameter);

    const cnpa_a = 0.1515 * l + 0.2505 * l1 + 0.0935 * l2 + 0.8986 * f2 + 0.6684 * f1 - 1.30;
    const cnpa_b = 0.1075 * l - 0.3719 * l1 + 0.1168 * l2 - 0.4803 * f2 + 0.1655 * f1 + 0.94;
    const cnpa_c = 0.0772 * l - 0.4703 * l1 + 1.2512 * l2 - 0.0506 * f2 - 0.2197 * f1 + 0.02;
    Cnpa[i] = cnpa_a * Math.atan(cnpa_b * (mi - 1) + cnpa_c)

    const alphaRadians = 1 * Math.PI / 180;
    if (Cd[i]) {
      Cna[i] = Cla[i] * Math.cos(alphaRadians) + Cd[i] * Math.sin(alphaRadians);
      Cmq[i] = -1 * Cna[i] * (((l * diameter) - (g1 * diameter)) / diameter) ** 2 - Cma[i]
      if (mi > 1) {
        Cmq[i] *= 2
      }
    }
    Clp[i] = estimateClp(mi, bullet.diameter, bullet.Ix, bullet.bearingSurfaceLength, bullet.noseLength)
  }

  return {
    Cd,
    Cnpa,
    Cla,
    Clp,
    Cma,
    Cda2,
    Cna,
    Cmq,
    z
  }
}

function estimateClp(ma, caliber, ix, bearingSurface, noseLength) {
  //console.log({ ma, caliber, ix, bearingSurface, noseLength })
  const wetlen = (bearingSurface + noseLength) / caliber
  const a = -0.0013041832 * ma ** 2 + 0.0131526182 * ma + -0.0505272693
  const b = 0.0037958439 * ma ** 2 + -0.0387777116 * ma + 0.1521346826
  const c = -0.0003363179 * ma ** 2 + 0.0035027927 * ma + -0.0141904271
  const d = -0.0001407522 * ma ** 2 + 0.0015084984 * ma + -0.0064200062
  const clp = a + b * caliber + c * ix + d * wetlen
  return clp
}
